import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { resetPassword } from "../lib/auth-client.ts";
import { PasswordField } from "../components/PasswordField.tsx";
import { AUTH_COPY, authResultError, normalizeAuthError, type AuthBanner } from "../lib/auth-messages.ts";

export function ResetPasswordPage() {
  const params = new URLSearchParams(window.location.search);
  const token = params.get("token") ?? "";
  const linkError = params.get("error");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(linkError ? AUTH_COPY.expiredResetToken : null);
  const [notice, setNotice] = useState<AuthBanner | null>(null);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setNotice(null);

    if (!token) {
      setError(AUTH_COPY.missingResetToken);
      return;
    }
    if (password !== confirmPassword) {
      setError(AUTH_COPY.passwordMismatch);
      return;
    }

    setLoading(true);
    try {
      const res = await resetPassword({ newPassword: password, token });
      const message = authResultError(res, "Could not reset your password. Request a new reset link and try again.");
      if (message) {
        setError(message);
        return;
      }
      setPassword("");
      setConfirmPassword("");
      setNotice({ kind: "success", text: AUTH_COPY.passwordResetComplete });
    } catch (err) {
      setError(normalizeAuthError(err, "Could not reset your password. Request a new reset link and try again."));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="auth-page">
      <div className="auth-wrap animate-fade-in">
        <div className="auth-logo">
          <div className="auth-logo-mark">
            <img src="/logo.webp" alt="CurrencyCore" />
          </div>
          <h1>Set new password</h1>
          <p>Choose a new password for your account</p>
        </div>

        <div className="auth-card">
          {error && <div className="auth-banner auth-banner-error">{error}</div>}
          {notice && <div className={`auth-banner auth-banner-${notice.kind}`}>{notice.text}</div>}

          <form onSubmit={handleSubmit} className="form-stack">
            <PasswordField
              id="new-password"
              label="New password"
              autoComplete="new-password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Min. 8 characters"
            />

            <PasswordField
              id="confirm-password"
              label="Confirm password"
              autoComplete="new-password"
              required
              minLength={8}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Repeat new password"
            />

            <button type="submit" disabled={loading || !token} className="btn btn-primary btn-block">
              {loading ? (
                <>
                  <span className="spinner" />
                  Saving…
                </>
              ) : (
                "Set new password"
              )}
            </button>
          </form>
        </div>

        <p className="auth-footer">
          <Link to="/login">Back to sign in</Link>
        </p>
      </div>
    </div>
  );
}
